import { ThemeProvider } from '@mui/system';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterLuxon } from '@mui/x-date-pickers-pro/AdapterLuxon';
import { LicenseInfo, muiXTelemetrySettings } from '@mui/x-license';
import { Font } from '@react-pdf/renderer';
import { Worker } from '@react-pdf-viewer/core';
import { enableMapSet } from 'immer';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
// Import side-effect modules first — fragments.ts registers GraphQL fragments
// that the module-level Apollo client (./lib/apolloClient) reads at construction
// time. Order matters: fragments must register before apolloClient is imported.
import './api/fragments';
import './lib/apolloClient';
import './lib/grafana';

import { FatalErrorBoundary, RedwoodProvider } from '@redwoodjs/web';
import { useMemo } from 'react';
import { RouterProvider } from 'react-aria-components';
import FatalErrorPage from 'src/pages/FatalErrorPage';
import Routes from 'src/Routes';

import 'material-symbols/outlined.css';
import { AuthProvider } from './auth';
import { AgrifulApolloProvider } from './lib/apolloClient';
import { createAriaRouterProps, createClerkRouterNavigation } from './lib/routing';
import { getAppRouter } from './routing/router';
import './index.css';
import theme from './lib/theme';
import { SearchKeyProvider } from './modules/entities/search';
import PrintManagerProvider from './modules/palletTags/providers/PrintManagerProvider';
import { PolicyProvider } from './modules/policy/PolicyProvider';
import AlgoliaContextProvider from './providers/AlgoliaProvider';
import { CubeTokenProvider } from './providers/CubeTokenProvider';
import { KnockNotificationProvider } from './providers/KnockNotificationProvider';

enableMapSet();

LicenseInfo.setLicenseKey(process.env.MUI_LICENSE_KEY ?? '');

// MUI X 9 ships an optional telemetry sender. Opt out at the same place the licence is set, so
// the two stay together. Licence validation is unaffected — this only stops the sender.
muiXTelemetrySettings.disableTelemetry();

// -- Register fonts for PDF generation
Font.register({
  family: 'Montserrat',
  fonts: [
    // Italic
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUFjIg1_i6t8kCHKm459Wx7xQYXK0vOoz6jq6R8aX8.ttf',
      fontStyle: 'italic',
      fontWeight: 100,
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUFjIg1_i6t8kCHKm459Wx7xQYXK0vOoz6jqyR9aX8.ttf',
      fontStyle: 'italic',
      fontWeight: 200,
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUFjIg1_i6t8kCHKm459Wx7xQYXK0vOoz6jq_p9aX8.ttf',
      fontStyle: 'italic',
      fontWeight: 300,
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUFjIg1_i6t8kCHKm459Wx7xQYXK0vOoz6jq6R9aX8.ttf',
      fontStyle: 'italic',
      fontWeight: 400,
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUFjIg1_i6t8kCHKm459Wx7xQYXK0vOoz6jq5Z9aX8.ttf',
      fontStyle: 'italic',
      fontWeight: 500,
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUFjIg1_i6t8kCHKm459Wx7xQYXK0vOoz6jq3p6aX8.ttf',
      fontStyle: 'italic',
      fontWeight: 600,
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUFjIg1_i6t8kCHKm459Wx7xQYXK0vOoz6jq0N6aX8.ttf',
      fontStyle: 'italic',
      fontWeight: 700,
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUFjIg1_i6t8kCHKm459Wx7xQYXK0vOoz6jqyR6aX8.ttf',
      fontStyle: 'italic',
      fontWeight: 800,
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUFjIg1_i6t8kCHKm459Wx7xQYXK0vOoz6jqw16aX8.ttf',
      fontStyle: 'italic',
      fontWeight: 900,
    },

    // Regular
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCtr6Uw-.ttf',
      fontStyle: 'normal',
      fontWeight: 100,
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCvr6Ew-.ttf',
      fontStyle: 'normal',
      fontWeight: 200,
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCs16Ew-.ttf',
      fontStyle: 'normal',
      fontWeight: 300,
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCtr6Ew-.ttf',
      fontStyle: 'normal',
      fontWeight: 400,
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCtZ6Ew-.ttf',
      fontStyle: 'normal',
      fontWeight: 500,
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCu170w-.ttf',
      fontStyle: 'normal',
      fontWeight: 600,
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCuM70w-.ttf',
      fontStyle: 'normal',
      fontWeight: 700,
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCvr70w-.ttf',
      fontStyle: 'normal',
      fontWeight: 800,
    },
    {
      src: 'https://fonts.gstatic.com/s/montserrat/v25/JTUHjIg1_i6t8kCHKm4532VJOt5-QNFgpCvC70w-.ttf',
      fontStyle: 'normal',
      fontWeight: 900,
    },
  ],
});
// -- Register fonts for PDF generation

Font.registerHyphenationCallback((word) => [word]);

// App-wide providers live here; route-specific providers belong in the code-based route tree wrappers.
// This is the app-root SDK wiring boundary: React Aria and Clerk both sit ABOVE the TanStack
// `RouterProvider`, so they cannot read the router from context and are handed it explicitly.
const App = () => {
  const router = getAppRouter();
  const ariaRouterProps = useMemo(() => createAriaRouterProps(router), [router]);
  const clerkNavigation = useMemo(() => createClerkRouterNavigation(router), [router]);

  return (
    <FatalErrorBoundary page={FatalErrorPage}>
      <RedwoodProvider titleTemplate="%PageTitle | %AppTitle">
        <RouterProvider {...ariaRouterProps}>
          <AuthProvider navigation={clerkNavigation}>
            <PolicyProvider>
              <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="en-US-u-fw-mon">
                  <AgrifulApolloProvider>
                    <KnockNotificationProvider>
                      <CubeTokenProvider>
                        <AlgoliaContextProvider>
                          <SearchKeyProvider>
                            <PrintManagerProvider>
                              {/* TODO: We should host this in our public folder ourselves. I do not trust CDNs after recent events... */}
                              <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                                <Routes />
                              </Worker>
                            </PrintManagerProvider>
                          </SearchKeyProvider>
                        </AlgoliaContextProvider>
                      </CubeTokenProvider>
                    </KnockNotificationProvider>
                  </AgrifulApolloProvider>
                </LocalizationProvider>
              </ThemeProvider>
            </PolicyProvider>
          </AuthProvider>
        </RouterProvider>
      </RedwoodProvider>
    </FatalErrorBoundary>
  );
};

const isE2E = process.env.AGRIFUL_ENV === 'e2e' || process.env.E2E_MODE === 'true';

// E2E: bootstrap all flags as true on the client, matching featureFlags.ts on the server, so client and server flag values stay consistent.
const e2eBootstrapFlags = {
  'permissions-can-create-purchase-orders': true,
  'permissions-can-view-purchase-orders': true,
  'permissions-can-create-sales-orders': true,
  'permissions-can-view-sales-orders': true,
  'permissions-can-view-inventory': true,
  'permissions-can-view-system-settings': true,
  workOrders: true,
  'buy-sell-orders': true,
  'expenses-charged-to-business-entities': true,
  'production-runs': true,
  'variable-product-weight': true,
  'check-printing': true,
  accounting: true,
  'order-board': true,
  customFields: true,
  'bi-dashboards': [],
  poSellThroughSo: true,
  'automation-settings': true,
  'grower-accounting-settings': true,
};

export default withLDProvider({
  clientSideID: process.env.LAUNCH_DARKLY_CLIENTSIDE_ID || (isE2E ? 'placeholder' : ''),
  context: { kind: 'user', anonymous: true },
  ...(isE2E && { options: { bootstrap: e2eBootstrapFlags } }),
})(App);
